import React, { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { ArrowRight, ArrowLeft, CheckCircle2, MapPin, Package, User } from "lucide-react";

const PRODUCT_TYPES = [
  "Flashing and Adhesives",
  "Decking",
  "Siding",
  "Hardware and Fasteners",
  "Insulation",
  "HouseWrap",
  "Exterior Doors",
  "Interior Doors",
  "Structural Hardware",
  "Tools",
  "Other",
];

const TIMELINES = ["Immediate (this week)", "1–2 weeks", "3–4 weeks", "Flexible (1+ months)"];

const STEPS = [
  { n: 1, label: "Where & What", icon: MapPin },
  { n: 2, label: "Specs", icon: Package },
  { n: 3, label: "Your Info", icon: User },
];

export default function RequestQuote() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(null);

  const presetProduct = params.get("product");
  const presetMfrId = params.get("manufacturer");

  const [form, setForm] = useState({
    contractor_name: "",
    company: "",
    email: "",
    phone: "",
    zip_code: "",
    product_type: (presetProduct && PRODUCT_TYPES.includes(presetProduct)) ? presetProduct : PRODUCT_TYPES[0],
    quantity: "",
    specifications: "",
    timeline: TIMELINES[0],
    budget: "",
    notes: presetMfrId ? `(Contractor specifically requested brand ID: ${presetMfrId})` : "",
    target_manufacturer_id: presetMfrId || "",
  });

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const validateStep = () => {
    if (step === 1) {
      if (!form.zip_code.trim() || form.zip_code.trim().length < 5) {
        toast.error("Please enter a valid 5-digit ZIP code.");
        return false;
      }
      if (!form.product_type || !form.quantity.trim()) {
        toast.error("Product type and quantity are required.");
        return false;
      }
    }
    if (step === 2) {
      if (!form.specifications.trim()) {
        toast.error("Please add specifications so dealers can quote accurately.");
        return false;
      }
    }
    if (step === 3) {
      if (!form.contractor_name.trim() || !form.email.trim() || !form.phone.trim()) {
        toast.error("Name, email and phone are required.");
        return false;
      }
    }
    return true;
  };

  const next = () => { if (validateStep()) setStep((s) => Math.min(3, s + 1)); };
  const back = () => setStep((s) => Math.max(1, s - 1));

  const submit = async () => {
    if (!validateStep()) return;
    setSubmitting(true);
    try {
      const { data } = await api.post("/leads", form);
      setSubmitted(data);
      toast.success("Lead submitted! Dealers in your area will be in touch.");
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Submission failed");
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="max-w-3xl mx-auto px-6 lg:px-10 py-20" data-testid="lead-success">
        <div className="brutal-card p-10">
          <CheckCircle2 className="text-[#C62828]" size={56} strokeWidth={1.5}/>
          <div className="label-tag mt-4">Lead Submitted</div>
          <h1 className="font-heading text-4xl md:text-5xl font-black uppercase mt-2 text-[#1A365D] leading-none">
            We&apos;re on it.
          </h1>
          <p className="mt-4 text-slate-700 leading-relaxed">
            Your request for <strong>{submitted.product_type}</strong> ({submitted.quantity}) in ZIP <strong>{submitted.zip_code}</strong> has been routed.
            Dealers and manufacturers in your market will contact you at <strong>{submitted.email}</strong> or <strong>{submitted.phone}</strong>.
          </p>
          <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-3 font-mono-ll text-xs">
            <div className="border-2 border-[#1A365D] p-3">
              <div className="text-slate-500 tracking-widest text-[10px]">LEAD ID</div>
              <div className="text-[#1A365D] truncate">{submitted.id}</div>
            </div>
            <div className="border-2 border-[#1A365D] p-3">
              <div className="text-slate-500 tracking-widest text-[10px]">STATUS</div>
              <div className="text-[#C62828] uppercase">{submitted.status}</div>
            </div>
            <div className="border-2 border-[#1A365D] p-3">
              <div className="text-slate-500 tracking-widest text-[10px]">SUBMITTED</div>
              <div className="text-[#1A365D]">{new Date(submitted.created_at).toLocaleString()}</div>
            </div>
          </div>
          <div className="mt-8 flex gap-3 flex-wrap">
            <button className="btn-secondary" onClick={() => { setSubmitted(null); setStep(1); setForm({...form, quantity:"", specifications:"", notes:""}); }} data-testid="submit-another-btn">
              Submit Another
            </button>
            <button className="btn-primary" onClick={() => navigate("/")} data-testid="back-home-btn">
              Back to Home
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-6 lg:px-10 py-14" data-testid="request-quote-page">
      <div className="label-tag">Contractor Quote Request</div>
      <h1 className="font-heading text-4xl md:text-6xl font-black uppercase mt-2 leading-none text-[#1A365D]">
        Tell us what you need.
      </h1>
      <p className="mt-3 text-slate-600">3 quick steps · about 90 seconds.</p>

      {/* Step pills (more polished than a plain progress bar) */}
      <div className="mt-8 grid grid-cols-3 gap-2" data-testid="step-indicator">
        {STEPS.map((s) => {
          const done = step > s.n;
          const active = step === s.n;
          return (
            <div key={s.n} className="flex flex-col gap-2" data-testid={`step-pill-${s.n}`}>
              <div className={`h-1.5 ${done || active ? "bg-[#C62828]" : "bg-slate-300"}`}/>
              <div className="flex items-center gap-2">
                <div className={`w-7 h-7 flex items-center justify-center text-xs font-mono-ll font-bold border-2 ${
                  done ? "bg-[#C62828] text-white border-[#C62828]"
                  : active ? "bg-[#1A365D] text-white border-[#1A365D]"
                  : "bg-white text-slate-400 border-slate-300"
                }`}>
                  {done ? <CheckCircle2 size={14}/> : s.n}
                </div>
                <div>
                  <div className={`font-mono-ll text-[10px] tracking-[0.25em] uppercase ${active ? "text-[#1A365D]" : "text-slate-500"}`}>
                    Step {s.n}
                  </div>
                  <div className={`text-sm font-bold ${active || done ? "text-[#1A365D]" : "text-slate-500"}`}>
                    {s.label}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <div className="brutal-card p-6 md:p-10 mt-8 fade-in" key={step}>
        {step === 1 && (
          <div className="space-y-5" data-testid="step-where-what">
            <div>
              <h2 className="font-heading text-2xl font-bold uppercase text-[#1A365D]">Where &amp; What</h2>
              <p className="text-sm text-slate-600 mt-1">We start with your job-site ZIP so we can route to local dealers right away.</p>
            </div>
            <div>
              <label className="label-tag block mb-2">Job Site ZIP Code *</label>
              <input
                className="ll-input"
                placeholder="78704"
                maxLength={5}
                inputMode="numeric"
                autoFocus
                value={form.zip_code}
                onChange={(e) => update("zip_code", e.target.value.replace(/\D/g, ""))}
                data-testid="zip-input"
              />
            </div>
            <div>
              <label className="label-tag block mb-2">Product Type *</label>
              <select className="ll-select" value={form.product_type} onChange={(e) => update("product_type", e.target.value)} data-testid="product-type-select">
                {PRODUCT_TYPES.map((p) => <option key={p}>{p}</option>)}
              </select>
            </div>
            <div>
              <label className="label-tag block mb-2">Quantity / Pieces *</label>
              <input className="ll-input" placeholder="e.g. 800 pcs of 2x6x12" value={form.quantity} onChange={(e) => update("quantity", e.target.value)} data-testid="quantity-input" />
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-5" data-testid="step-specs">
            <div>
              <h2 className="font-heading text-2xl font-bold uppercase text-[#1A365D]">Specs &amp; Timing</h2>
              <p className="text-sm text-slate-600 mt-1">The more specific you are, the faster dealers can quote you back.</p>
            </div>
            <div>
              <label className="label-tag block mb-2">Specifications *</label>
              <textarea
                className="ll-textarea"
                rows={5}
                placeholder="Grade, species, treatment, dimensions, certifications…"
                value={form.specifications}
                onChange={(e) => update("specifications", e.target.value)}
                data-testid="specifications-input"
              />
            </div>
            <div className="grid md:grid-cols-2 gap-5">
              <div>
                <label className="label-tag block mb-2">Timeline *</label>
                <select className="ll-select" value={form.timeline} onChange={(e) => update("timeline", e.target.value)} data-testid="timeline-select">
                  {TIMELINES.map((t) => <option key={t}>{t}</option>)}
                </select>
              </div>
              <div>
                <label className="label-tag block mb-2">Budget (Optional)</label>
                <input className="ll-input" placeholder="$ — leave blank if you&apos;d rather not say" value={form.budget} onChange={(e) => update("budget", e.target.value)} data-testid="budget-input" />
              </div>
            </div>
          </div>
        )}

        {step === 3 && (
          <div className="space-y-5" data-testid="step-contact">
            <div>
              <h2 className="font-heading text-2xl font-bold uppercase text-[#1A365D]">Your Contact Info</h2>
              <p className="text-sm text-slate-600 mt-1">Last step. We&apos;ll only share this with dealers we route your lead to.</p>
            </div>
            <div className="grid md:grid-cols-2 gap-5">
              <div>
                <label className="label-tag block mb-2">Full Name *</label>
                <input className="ll-input" value={form.contractor_name} onChange={(e) => update("contractor_name", e.target.value)} data-testid="contractor-name-input" />
              </div>
              <div>
                <label className="label-tag block mb-2">Company</label>
                <input className="ll-input" value={form.company} onChange={(e) => update("company", e.target.value)} data-testid="company-input" />
              </div>
              <div>
                <label className="label-tag block mb-2">Email *</label>
                <input className="ll-input" type="email" value={form.email} onChange={(e) => update("email", e.target.value)} data-testid="email-input" />
              </div>
              <div>
                <label className="label-tag block mb-2">Phone *</label>
                <input className="ll-input" value={form.phone} onChange={(e) => update("phone", e.target.value)} data-testid="phone-input" />
              </div>
            </div>
            <div>
              <label className="label-tag block mb-2">Notes (Optional)</label>
              <textarea className="ll-textarea" rows={2} value={form.notes} onChange={(e) => update("notes", e.target.value)} data-testid="notes-input" />
            </div>

            {/* Inline mini-review — eliminates a separate review step */}
            <div className="mt-6 border-t-2 border-slate-200 pt-5">
              <div className="label-tag mb-3">Quick Review</div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
                <MiniReview label="ZIP" value={form.zip_code || "—"}/>
                <MiniReview label="Product" value={form.product_type}/>
                <MiniReview label="Quantity" value={form.quantity || "—"}/>
                <MiniReview label="Timeline" value={form.timeline}/>
              </div>
            </div>
          </div>
        )}

        <div className="mt-10 flex justify-between gap-3">
          <button className="btn-ghost flex items-center gap-2" disabled={step === 1} onClick={back} data-testid="step-back-btn">
            <ArrowLeft size={16}/> Back
          </button>
          {step < 3 ? (
            <button className="btn-primary flex items-center gap-2" onClick={next} data-testid="step-next-btn">
              {step === 1 ? "Continue to Specs" : "Continue to Contact"} <ArrowRight size={16}/>
            </button>
          ) : (
            <button className="btn-primary flex items-center gap-2" onClick={submit} disabled={submitting} data-testid="submit-lead-btn">
              {submitting ? "Submitting…" : "Submit Quote Request"} <ArrowRight size={16}/>
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function MiniReview({ label, value }) {
  return (
    <div className="border border-slate-300 p-2 bg-slate-50">
      <div className="font-mono-ll text-[9px] tracking-widest uppercase text-slate-500">{label}</div>
      <div className="text-[#1A365D] font-semibold truncate">{value}</div>
    </div>
  );
}
